import React, { useState } from "react";
import {
  FaBaby,
  FaAppleAlt,
  FaBookMedical,
  FaHeart,
  FaTimes,
  FaHome
} from "react-icons/fa";
import { Link } from "react-router-dom";
import month1 from "../assets/images/1 month.png";
import month2 from "../assets/images/2 month.png";
import month3 from "../assets/images/3 month.png";
import month4 from "../assets/images/4 month.png";
import month5 from "../assets/images/5 month.png";
import month6 from "../assets/images/6 month.png";
import month7 from "../assets/images/7 month.png";
import month8 from "../assets/images/8 month.png";
import month9 from "../assets/images/9 month.png";
import { Helmet } from "react-helmet-async";

const PregnancyTracker = () => {
  const [activeMonth, setActiveMonth] = useState(0);

  const pregnancyData = [
    {
      month: 1,
      title: "Conception & Early Development",
      babySize: "Poppy seed",
      tips: [
        "Start prenatal vitamins (especially folic acid)",
        "Avoid alcohol, smoking, and raw foods",
        "Schedule your first prenatal visit",
      ],
      nutrition: [
        "Increase folate-rich foods (leafy greens, lentils)",
        "Stay hydrated (8-10 glasses water/day)",
        "Small frequent meals for nausea",
      ],
      dos: [
        "Take 400-800mcg folic acid daily",
        "Rest when fatigued",
        "Track menstrual cycle dates",
      ],
      donts: [
        "Don't take hot baths/saunas",
        "Avoid deli meats and unpasteurized dairy",
        "No heavy lifting",
      ],
      image: month1,
    },
    {
      month: 2,
      title: "Embryo Growth",
      babySize: "Blueberry",
      tips: [
        "Manage morning sickness with ginger",
        "Begin gentle pregnancy exercises",
        "Avoid strong smells that trigger nausea",
      ],
      nutrition: [
        "Protein-rich snacks (nuts, Greek yogurt)",
        "Vitamin B6 for nausea (bananas, chickpeas)",
        "Iron from lean meats/beans",
      ],
      dos: [
        "Eat every 2-3 hours",
        "Start pelvic floor exercises",
        "Wear loose, comfortable clothing",
      ],
      donts: [
        "Don't clean cat litter (toxoplasmosis risk)",
        "Avoid sushi with raw fish",
        "No intense cardio workouts",
      ],
      image: month2,
    },
    {
      month: 3,
      title: "First Trimester Milestones",
      babySize: "Lime",
      tips: [
        "Start using a pregnancy-safe moisturizer for stretching skin",
        "Book a dating ultrasound if not already done",
        "Discuss prenatal screening options with doctor",
      ],
      nutrition: [
        "Add calcium-rich foods (dairy, broccoli, almonds)",
        "Eat high-fiber foods to avoid constipation",
        "Ensure adequate protein intake",
      ],
      dos: [
        "Sleep on your side when possible",
        "Track weight gain and symptoms",
        "Keep up with hydration",
      ],
      donts: [
        "Don't consume too much caffeine",
        "Avoid medications not approved by OB",
        "No smoking or secondhand smoke exposure",
      ],
      image: month3,
    },
    {
      month: 4,
      title: "Second Trimester Begins",
      babySize: "Avocado",
      tips: [
        "Start looking into prenatal classes",
        "Begin light stretching/yoga for flexibility",
        "Document pregnancy with journal/photos",
      ],
      nutrition: [
        "Continue iron-rich diet",
        "Add omega-3s (chia seeds, flaxseed, fish)",
        "Stay consistent with prenatal vitamins",
      ],
      dos: [
        "Attend your anatomy scan around week 18-20",
        "Use support pillows while sleeping",
        "Moisturize belly to prevent itching",
      ],
      donts: [
        "Don't skip meals",
        "Avoid lying flat on your back too long",
        "Don't wear tight clothing",
      ],
      image: month4,
    },
    {
      month: 5,
      title: "Feeling Baby's First Movements",
      babySize: "Banana",
      tips: [
        "Pay attention to early flutters (quickening)",
        "Begin researching baby gear and nursery",
        "Start gentle walking routines",
      ],
      nutrition: [
        "Eat enough iron and vitamin C for blood volume",
        "Limit sugary cravings with fruit alternatives",
        "Try calcium-fortified juices",
      ],
      dos: [
        "Monitor baby movements over time",
        "Practice proper posture",
        "Use a body pillow for sleep comfort",
      ],
      donts: [
        "Don't overdo physical activity",
        "Avoid overly processed snacks",
        "No high-sodium foods",
      ],
      image: month5,
    },
    {
      month: 6,
      title: "Building Baby's Senses",
      babySize: "Papaya",
      tips: [
        "Talk and sing to your baby regularly",
        "Wear maternity support wear for belly/back",
        "Hydrate well to reduce swelling",
      ],
      nutrition: [
        "Include DHA-rich foods or supplements",
        "Avoid empty-calorie snacks",
        "Stay balanced with carbs and proteins",
      ],
      dos: [
        "Attend glucose screening test",
        "Track changes in movement and kicks",
        "Practice relaxation breathing",
      ],
      donts: [
        "Don't stand for long periods without breaks",
        "Avoid caffeine late in the day",
        "No lifting heavy grocery bags or toddlers",
      ],
      image: month6,
    },
    {
      month: 7,
      title: "Third Trimester Starts",
      babySize: "Eggplant",
      tips: [
        "Start planning your birth preferences",
        "Discuss maternity leave and hospital bag items",
        "Practice side-lying positions for comfort",
      ],
      nutrition: [
        "Increase magnesium (nuts, spinach, whole grains)",
        "Watch sodium to reduce swelling",
        "Eat small meals more frequently",
      ],
      dos: [
        "Count baby kicks daily",
        "Take prenatal classes (if not already done)",
        "Sleep with legs elevated to reduce swelling",
      ],
      donts: [
        "Don't ignore unusual symptoms (e.g. blurry vision)",
        "Avoid flying unless cleared by your doctor",
        "No skipping doctor visits",
      ],
      image: month7,
    },
    {
      month: 8,
      title: "Final Preparation Phase",
      babySize: "Pineapple",
      tips: [
        "Create a birth plan and share with your care provider",
        "Install your baby car seat early",
        "Start perineal massage (ask OB for guidance)",
      ],
      nutrition: [
        "Increase fiber to prevent constipation",
        "Eat protein for strength and stamina",
        "Drink water to stay hydrated despite frequent urination",
      ],
      dos: [
        "Pack your hospital bag",
        "Practice labor positions and breathing",
        "Finish nursery setup",
      ],
      donts: [
        "Don't stress about due date changes",
        "Avoid spicy or greasy food that causes reflux",
        "Don't forget regular fetal monitoring",
      ],
      image: month8,
    },
    {
      month: 9,
      title: "Final Preparations",
      babySize: "Watermelon",
      tips: [
        "Practice relaxation techniques",
        "Install car seat",
        "Finalize birth plan",
      ],
      nutrition: [
        "High-fiber foods for constipation",
        "Omega-3s for brain development",
        "Calcium-rich foods (3-4 servings/day)",
      ],
      dos: [
        "Do perineal massage",
        "Pack hospital bag",
        "Monitor fetal movements",
      ],
      donts: [
        "Don't ignore signs of preeclampsia",
        "Avoid long trips",
        "No strenuous activity",
      ],
      image: month9,
    },
  ];


  return (
    <div className="w-full font-nunito min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 py-4 sm:py-8">
<Helmet>
    {/* Google Analytics */}
    <script
      async
      src="https://www.googletagmanager.com/gtag/js?id=G-YR8YMW9ERY"
    ></script>
    <script>
      {`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'G-YR8YMW9ERY', {
          page_path: window.location.pathname,
        });
      `}
    </script>
    <title>Pregnancy Tracker - FlowHearts | Fetal Development & Prenatal Tips</title>
    <meta
      name="description"
      content="Track your pregnancy month-by-month with FlowHearts' Pregnancy Tracker. Get fetal development insights, nutrition tips, and prenatal care advice."
    />

    {/* Canonical URL */}
    <link rel="canonical" href="https://flowhearts.com/pregnancy-tracker" />

    {/* Open Graph / Facebook */}
    <meta property="og:type" content="website" />
    <meta property="og:url" content="https://flowhearts.com/pregnancy-tracker" />
    <meta
      property="og:title"
      content="Pregnancy Tracker - FlowHearts | Fetal Development & Prenatal Tips"
    />
    <meta
      property="og:description"
      content="Monitor your baby's growth with FlowHearts' Pregnancy Tracker, offering monthly fetal development updates, nutrition advice, and prenatal care tips."
    />
    <meta
      property="og:image"
      content="https://flowhearts.com/images/pregnancy-journey-preview.jpg"
    />

    {/* Twitter */}
    <meta property="twitter:card" content="summary_large_image" />
    <meta property="twitter:url" content="https://flowhearts.com/pregnancy-tracker" />
    <meta
      property="twitter:title"
      content="Pregnancy Tracker - FlowHearts | Fetal Development & Prenatal Tips"
    />
    <meta
      property="twitter:description"
      content="Follow your pregnancy journey with FlowHearts' Tracker, featuring fetal development milestones, prenatal nutrition, and essential care tips."
    />
    <meta
      property="twitter:image"
      content="https://flowhearts.com/images/pregnancy-journey-preview.jpg"
    />

    {/* Structured Data */}
    <script type="application/ld+json">
      {`
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "FlowHearts Pregnancy Tracker",
          "url": "https://flowhearts.com/pregnancy-tracker",
          "description": "Track pregnancy progress with FlowHearts' Pregnancy Tracker, including fetal development, nutrition tips, and prenatal care guidance.",
          "applicationCategory": "HealthApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "creator": {
            "@type": "Organization",
            "name": "FlowHearts"
          }
        }
      `}
    </script>
  </Helmet>
      <div className="max-w-8xl mx-auto px-3 sm:px-6">
        {/* Back to Home Button - More compact on mobile */}
        <div className="flex justify-start mb-4 sm:mb-6">
          <Link
            to="/"
            className="flex items-center gap-1 px-3 py-1.5 sm:px-4 sm:py-2 bg-white rounded-full shadow-md hover:bg-pink-50 transition-colors duration-200 text-xs sm:text-base"
          >
            <FaHome className="text-pink-500 text-sm sm:text-base" />
            <span className="text-purple-700 font-medium sm:inline">Back to Home</span>
          </Link>
        </div>

        <div className="bg-white rounded-xl shadow-md p-3 sm:p-6">
          {/* Header - More compact on mobile */}
          <header className="text-center mb-4 sm:mb-8">
            <h2 className="text-xl font-cormorant sm:text-3xl font-bold text-purple-800 mb-1 sm:mb-2 flex items-center justify-center">
              <FaBaby className="mr-2 sm:mr-3 text-pink-500 text-2xl sm:text-4xl" />
              Pregnancy Journey
            </h2>
            <p className="text-sm sm:text-xl text-pink-600">Track your baby's growth</p>
          </header>

          {/* Mobile View - Tightly integrated vertical progress + content */}
          <div className="sm:hidden flex flex-row gap-2 mb-4">
            {/* Vertical Progress Bar - More compact */}
            <div className="w-12 flex flex-col items-center">
              <div className="relative h-full w-0.5 bg-purple-100">
                <div 
                  className="absolute top-0 left-0 w-0.5 bg-pink-500 transition-all duration-300"
                  style={{ height: `${(activeMonth / 8) * 100}%` }}
                ></div>
              </div>
              <div className="flex flex-col items-center justify-between h-full py-1">
                {pregnancyData.map((month, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveMonth(index)}
                    className={`my-1 ${
                      index === activeMonth ? 'text-purple-800' : 'text-gray-400'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                      index === activeMonth 
                        ? 'bg-pink-500 text-white shadow-md'
                        : 'bg-purple-100'
                    }`}>
                      {month.month}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Mobile Content Preview - Takes remaining space */}
            <div className="flex-1 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-3 border border-purple-100">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-base font-bold text-purple-800">
                  Month {pregnancyData[activeMonth].month}
                </h3>
                <p className="text-xs text-pink-600 bg-pink-100 px-2 py-1 rounded-full">
                  {pregnancyData[activeMonth].babySize}
                </p>
              </div>
              <p className="text-xs text-purple-700 line-clamp-2">
                {pregnancyData[activeMonth].tips[0]}
              </p>
            </div>
          </div>

          {/* Desktop View - Unchanged horizontal progress */}
          <div className="hidden sm:block relative mb-10">
          <div className="absolute left-0 right-0 top-1/2 h-1 bg-purple-100 transform -translate-y-1/2"></div>
            <div
              className="absolute left-0 top-1/2 h-1 bg-pink-500 transform -translate-y-1/2 transition-all duration-300"
              style={{ width: `${(activeMonth / 8) * 100}%` }}
            ></div>
            <div className="flex justify-between relative z-10">
              {pregnancyData.map((month, index) => (
                <button
                  key={index}
                  onClick={() => setActiveMonth(index)}
                  className={`flex flex-col items-center w-16 ${
                    index < activeMonth
                      ? "text-pink-600"
                      : index === activeMonth
                      ? "text-purple-800"
                      : "text-gray-400"
                  }`}
                >
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all ${
                      index === activeMonth
                        ? "bg-pink-500 text-white scale-125 shadow-lg"
                        : index < activeMonth
                        ? "bg-pink-200 text-pink-700"
                        : "bg-purple-100 text-purple-400"
                    }`}
                  >
                    {month.month}
                  </div>
                  <span className="text-xs font-medium">{month.babySize}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Main Content - More compact on mobile */}
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-3 sm:p-6 border border-purple-100">
            <div className="flex flex-col md:flex-row gap-3 sm:gap-6">
              {/* Left Column - Image */}
              <div className="md:w-2/5">
                <div className="bg-white p-2 sm:p-3 rounded-lg shadow-sm border border-purple-200">
                  <img
                    src={pregnancyData[activeMonth].image}
                    alt={`Month ${activeMonth + 1}`}
                    className="w-full h-auto object-contain rounded-lg"
                  />
                  <div className="mt-2 sm:mt-4 text-center">
                    <h3 className="text-base sm:text-xl font-bold text-purple-800">
                      Month {pregnancyData[activeMonth].month}
                    </h3>
                    <p className="text-xs sm:text-base text-pink-600">
                      Size: {pregnancyData[activeMonth].babySize}
                    </p>
                  </div>
                </div>
              </div>

              {/* Right Column - Details */}
              <div className="md:w-3/5 space-y-2 sm:space-y-4">
                {/* Sections with tighter spacing on mobile */}
                <div className="bg-white p-2 sm:p-4 rounded-lg shadow-sm border border-pink-100">
                  <h4 className="font-semibold text-purple-800 mb-1 sm:mb-3 flex items-center text-sm sm:text-base">
                    <FaBookMedical className="mr-1 sm:mr-2 text-pink-500 text-sm sm:text-base" />
                    Quick Tips
                  </h4>
                  <ul className="list-disc pl-4 space-y-1 text-xs sm:text-sm">
                    {pregnancyData[activeMonth].tips.slice(0, 2).map((tip, i) => (
                      <li key={i} className="text-purple-700">{tip}</li>
                    ))}
                  </ul>
                </div>

                {/* Nutrition */}
                <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-green-100">
                  <h4 className="font-semibold text-green-700 mb-2 sm:mb-3 flex items-center">
                    <FaAppleAlt className="mr-2 text-green-500" />
                    Nutrition Focus
                  </h4>
                  <ul className="list-disc pl-5 space-y-1 text-xs sm:text-sm">
                    {pregnancyData[activeMonth].nutrition.map((item, i) => (
                      <li key={i} className="text-green-700">
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Dos & Don'ts */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-blue-100">
                    <h4 className="font-semibold text-blue-700 mb-2 sm:mb-3 flex items-center">
                      <FaHeart className="mr-2 text-blue-500" />
                      Do's
                    </h4>
                    <ul className="space-y-1 sm:space-y-2">
                      {pregnancyData[activeMonth].dos.map((item, i) => (
                        <li key={i} className="flex items-start">
                          <span className="bg-blue-100 p-1 rounded-full mr-2">
                            <FaHeart className="text-blue-600 text-xs" />
                          </span>
                          <span className="text-xs sm:text-sm text-blue-800">
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border border-pink-100">
                    <h4 className="font-semibold text-pink-700 mb-2 sm:mb-3 flex items-center">
                      <FaTimes className="mr-2 text-pink-500" />
                      Don'ts
                    </h4>
                    <ul className="space-y-1 sm:space-y-2">
                      {pregnancyData[activeMonth].donts.map((item, i) => (
                        <li key={i} className="flex items-start">
                          <span className="bg-pink-100 p-1 rounded-full mr-2">
                            <FaTimes className="text-pink-600 text-xs" />
                          </span>
                          <span className="text-xs sm:text-sm text-pink-800">
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Buttons - More compact on mobile */}
          <div className="flex justify-between mt-4 sm:mt-6">
            <button
              onClick={() => setActiveMonth(Math.max(0, activeMonth - 1))}
              disabled={activeMonth === 0}
              className={`px-2 sm:px-4 py-1 text-xs sm:text-base rounded-lg font-medium ${
                activeMonth === 0
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
              }`}
            >
              Previous
            </button>
            <button
              onClick={() => setActiveMonth(Math.min(8, activeMonth + 1))}
              disabled={activeMonth === 8}
              className={`px-2 sm:px-4 py-1 text-xs sm:text-base rounded-lg font-medium ${
                activeMonth === 8
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-pink-100 text-pink-700 hover:bg-pink-200'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      </div>
      {/* Educational Section */}
      <div className="max-w-8xl mx-auto px-3 sm:px-6 mt-8 sm:mt-12">
        <div className="bg-white rounded-3xl shadow-xl p-4 sm:p-8">
          {/* How to Use Section */}
          <div className="mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaBaby className="mr-3 text-pink-500" />
              How to Use the Pregnancy Journey Tracker
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-pink-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  1
                </div>
                <h3 className="text-lg font-semibold text-center text-pink-700 mb-2">
                  Select a Month
                </h3>
                <p className="text-gray-700 text-center">
                  Click on any month in the progress bar to view specific fetal development details and tips.
                </p>
              </div>

              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <div className="w-12 h-12 bg-purple-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  2
                </div>
                <h3 className="text-lg font-semibold text-center text-purple-700 mb-2">
                  Explore Content
                </h3>
                <p className="text-gray-700 text-center">
                  Review baby size, nutrition advice, do's and don'ts for that stage of pregnancy.
                </p>
              </div>

              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <div className="w-12 h-12 bg-pink-500 text-white rounded-full flex items-center justify-center text-xl mb-4 mx-auto">
                  3
                </div>
                <h3 className="text-lg font-semibold text-center text-pink-700 mb-2">
                  Navigate Months
                </h3>
                <p className="text-gray-700 text-center">
                  Use previous/next buttons to progress through your pregnancy milestones sequentially.
                </p>
              </div>
            </div>
          </div>

          {/* Understanding Fetal Development */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaBaby className="mr-3 text-purple-500" />
              Understanding Fetal Development
            </h2>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200 mb-6">
              <h3 className="text-xl font-semibold text-purple-800 mb-4">
                Key Growth Stages
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="bg-white p-4 rounded-lg">
                  <p className="font-semibold text-pink-600 mb-2">First Trimester</p>
                  <p className="text-gray-700">
                    Rapid cell division forms major organs and structures, setting the foundation for baby growth.
                  </p>
                </div>

                <div className="bg-white p-4 rounded-lg">
                  <p className="font-semibold text-purple-600 mb-2">Second Trimester</p>
                  <p className="text-gray-700">
                    Sensory development advances with hearing and movement, as the fetus becomes more active.
                  </p>
                </div>

                <div className="bg-white p-4 rounded-lg">
                  <p className="font-semibold text-green-600 mb-2">Third Trimester</p>
                  <p className="text-gray-700">
                    Focus on lung maturation and weight gain, preparing for life outside the womb.
                  </p>
                </div>
              </div>

              <p className="text-gray-700">
                Monitoring these pregnancy milestones helps expectant parents connect with their baby's progress and supports informed prenatal care decisions.
              </p>
            </div>
          </div>

          {/* Importance of Prenatal Nutrition */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaAppleAlt className="mr-3 text-green-500" />
              Importance of Prenatal Nutrition
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                <h3 className="text-lg font-semibold text-green-700 mb-3">
                  Essential Nutrients
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <FaAppleAlt className="text-green-500 mr-2 mt-1 text-xs" />
                    <span>Folic acid for neural tube development</span>
                  </li>
                  <li className="flex items-start">
                    <FaAppleAlt className="text-green-500 mr-2 mt-1 text-xs" />
                    <span>Iron to support increased blood volume</span>
                  </li>
                  <li className="flex items-start">
                    <FaAppleAlt className="text-green-500 mr-2 mt-1 text-xs" />
                    <span>Calcium for bone formation</span>
                  </li>
                </ul>
              </div>

              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <h3 className="text-lg font-semibold text-pink-700 mb-3">
                  Benefits for Mom & Baby
                </h3>
                <p className="text-gray-700 mb-3">
                  Proper nutrition during pregnancy promotes healthy weight gain and reduces complications.
                </p>
                <p className="text-gray-700">
                  It supports maternal health while ensuring optimal fetal growth throughout each trimester.
                </p>
              </div>
            </div>
          </div>

          {/* Note on Professional Advice */}
          <div className="bg-white rounded-xl p-6 shadow-inner border border-pink-300 mb-12">
            <h3 className="text-xl font-semibold text-pink-800 mb-4 flex items-center">
              <FaBookMedical className="mr-2 text-pink-500" />
              Professional Advice
            </h3>
            <p className="text-gray-700 mb-3">
              This tracker provides general guidance on pregnancy stages. Individual needs may vary based on health factors.
            </p>
            <p className="text-gray-700">
              Consult your healthcare provider for personalized prenatal care and to address any specific concerns during your journey.
            </p>
          </div>

          {/* New Section: Why Track Your Pregnancy? */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaBaby className="mr-3 text-pink-500" />
              Why Track Your Pregnancy Journey?
            </h2>
            <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-xl p-6 border border-pink-200">
              <p className="text-gray-700 mb-4">
                In today's emphasis on mindful parenting, tracking fetal development offers more than just information—it's a way to bond and prepare thoughtfully.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <FaBookMedical className="text-purple-500 mr-2 mt-1 flex-shrink-0" />
                  <span><strong>Health Monitoring:</strong> Stay aware of key pregnancy milestones to spot any unusual changes early.</span>
                </li>
                <li className="flex items-start">
                  <FaBookMedical className="text-purple-500 mr-2 mt-1 flex-shrink-0" />
                  <span><strong>Emotional Connection:</strong> Visualize baby growth to strengthen the parent-child bond before birth.</span>
                </li>
                <li className="flex items-start">
                  <FaBookMedical className="text-purple-500 mr-2 mt-1 flex-shrink-0" />
                  <span><strong>Lifestyle Guidance:</strong> Receive tailored tips on nutrition and activities for optimal maternal health.</span>
                </li>
                <li className="flex items-start">
                  <FaBookMedical className="text-purple-500 mr-2 mt-1 flex-shrink-0" />
                  <span><strong>Preparation Tool:</strong> Use insights to plan for each stage, from prenatal classes to nursery setup.</span>
                </li>
              </ul>
              <p className="text-gray-700 mt-4">
                Embracing this tracker aligns with current trends in proactive prenatal care, empowering you throughout the experience.
              </p>
            </div>
          </div>

          {/* New Section: Frequently Asked Questions */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaBookMedical className="mr-3 text-purple-500" />
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-700 mb-2">
                  When does pregnancy tracking start?
                </h3>
                <p className="text-gray-700">
                  It begins from conception, with months aligned to standard gestational timelines for accurate progress.
                </p>
              </div>
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <h3 className="text-lg font-semibold text-pink-700 mb-2">
                  How accurate are baby size comparisons?
                </h3>
                <p className="text-gray-700">
                  These are approximate averages; actual sizes vary, but they help visualize development stages effectively.
                </p>
              </div>
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-700 mb-2">
                  Can nutrition tips replace medical advice?
                </h3>
                <p className="text-gray-700">
                  No, these are general suggestions. Always consult professionals for personalized maternal health plans.
                </p>
              </div>
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <h3 className="text-lg font-semibold text-pink-700 mb-2">
                  What if my pregnancy differs from the tracker?
                </h3>
                <p className="text-gray-700">
                  Every journey is unique. Use this as a guide and discuss variations with your healthcare provider.
                </p>
              </div>
            </div>
          </div>

          {/* New Section: Essential Pregnancy Tips */}
          <div>
            <h2 className="text-3xl font-bold font-cormorant text-purple-800 mb-6 flex items-center">
              <FaHeart className="mr-3 text-pink-500" />
              Essential Pregnancy Tips
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <h3 className="text-lg font-semibold text-pink-700 mb-2">
                  Stay Active
                </h3>
                <p className="text-gray-700">
                  Incorporate safe exercises like walking or swimming to support physical well-being during each trimester.
                </p>
              </div>
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-700 mb-2">
                  Manage Stress
                </h3>
                <p className="text-gray-700">
                  Practice mindfulness or prenatal yoga to maintain emotional balance throughout your journey.
                </p>
              </div>
              <div className="bg-pink-50 rounded-xl p-6 border border-pink-200">
                <h3 className="text-lg font-semibold text-pink-700 mb-2">
                  Build Support
                </h3>
                <p className="text-gray-700">
                  Connect with other expectant parents through classes or online communities for shared experiences.
                </p>
              </div>
              <div className="bg-purple-50 rounded-xl p-6 border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-700 mb-2">
                  Monitor Progress
                </h3>
                <p className="text-gray-700">
                  Keep a journal of symptoms and milestones to discuss during prenatal visits.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PregnancyTracker;